import { isScpQuery } from '@blisspointmedia/bpm-types/dist/querying';
import { GetCustomMetricsResponse } from '@blisspointmedia/bpm-types/dist/CustomMetrics';
import {
  ChannelId,
  ChartConfig,
  CustomMetricDisplayMeta,
  CustomMetricFormatterId
} from '@blisspointmedia/bpm-types/dist/dashboarding';

// The pure half of useFieldMetaCtx — everything that turns raw `custom_metrics` rows and a
// ChartConfig into the shapes getFieldMeta reads. Split out so it can be tested without standing up
// react-query, and because the channel-keying below is the load-bearing part of the feature.

export interface ChannelCustomMetrics {
  channel: ChannelId;
  metrics: Record<string, CustomMetricDisplayMeta>;
}

// `custom_metrics.formatter` is a plain `string` column with nothing enforcing the union at the DB
// level, so narrow it with a guard. bpm-types' CustomMetricFormatterId explicitly documents that a
// raw row value must never be `as`-cast into the union — a typo from a manual edit would otherwise
// flow through as a false union member and reach a switch that cannot see it.
export const isFormatterId = (raw: string): raw is CustomMetricFormatterId =>
  raw === 'percent' || raw === 'spend' || raw === 'number';

// `function_string` is AUTHORING syntax ("fetch.clicks / fetch.impressions"), not display prose.
// Strip the `fetch.` namespace and pass the rest through verbatim. Deliberately NOT pretty-named
// against the registry: showing the author the expression they typed is honest, whereas a
// reconstruction risks misrepresenting their own metric.
//
// Case-INSENSITIVE, matching bpm-types' canonical stripper (`customMetricValidation.ts`'s
// `stripFetchPrefix`, `/^fetch\./i`): a `FETCH.` prefix would otherwise survive into the display
// formula. `\b` still protects `refetch.count` from a false match.
const toDisplayFormula = (functionString: string): string | undefined =>
  functionString?.replace(/\bfetch\./gi, '').trim() || undefined;

// One channel's rows as the display metadata getFieldMeta reads, keyed by the metric's `name` — its
// permanent, frozen id, which is what a saved ChartConfig references. NOT display_name, which is the
// field a rename edits.
export const toDisplayMap = (
  rows: GetCustomMetricsResponse
): Record<string, CustomMetricDisplayMeta> =>
  Object.fromEntries(
    rows.map(row => [
      row.name,
      {
        label: row.display_name,
        formatter: isFormatterId(row.formatter) ? row.formatter : 'number',
        decimals: row.decimals,
        // Both optional: `description` is nullable in practice, and absent must mean "fall through
        // to the registry's own description" rather than "render an empty tooltip".
        description: row.description?.trim() || undefined,
        displayFormula: toDisplayFormula(row.function_string)
      }
    ])
  );

// The distinct channels this chart's queries read. A chart can legitimately span several — a platform
// series and a source-of-truth series, say — and custom metrics are scoped per channel, so each one
// is fetched separately. Custom metrics are SCP-only, so a Retail Metrics query contributes none.
export const chartChannels = (config: ChartConfig): ChannelId[] =>
  Array.from(
    new Set(config.queries.flatMap(query => (isScpQuery(query) ? [query.channel.id] : [])))
  );

// Which channel each query reads, so getFieldMeta can resolve a field's channel from the `queryId`
// half of its OutputFieldRef ("q0" in "q0.spend") without every render site threading one. Retail
// Metrics queries are skipped, as above; getFieldMeta falls back to the registry without a channel.
export const buildChannelByQueryId = (config: ChartConfig): Record<string, ChannelId> =>
  Object.fromEntries(
    config.queries.flatMap(query =>
      isScpQuery(query) ? [[query.queryId, query.channel.id] as const] : []
    )
  );

// Fold the per-channel results into the channel-keyed shape getFieldMeta reads. Kept at module scope
// so its identity is stable: react-query re-runs `combine` whenever the function changes, and an
// inline one would produce a new object every render, defeating the memo in the hook.
export const combineByChannel = (
  results: { data?: ChannelCustomMetrics }[]
): Partial<Record<ChannelId, Record<string, CustomMetricDisplayMeta>>> => {
  const byChannel: Partial<Record<ChannelId, Record<string, CustomMetricDisplayMeta>>> = {};
  for (const { data } of results) {
    if (data) {
      byChannel[data.channel] = data.metrics;
    }
  }
  return byChannel;
};
